import React, { useEffect, useRef, useCallback, useMemo, useState, useImperativeHandle, forwardRef } from 'react';

import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { AntGlobal, AntSession } from '../../utils/AntContext';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';

import './index.scss';
import AntErrorPage from '../../components/AntErrorPage';
import AntTextToSpeech from '../../components/AntTextToSpeech';

import HeaderArea from './HeaderArea';
import UtilsTabs from './UtilsTabs';
import AntPOSVoucher from '../AntPOSVoucher';
import Splitter from './Splitter';
import { getHeaderRightWidth } from './HeaderArea/tabLayout';

const STORAGE_KEY_RIGHT_W = 'ant_pos_screen_right_w';
const MIN_RIGHT_W = 400;
const MIN_LEFT_W = 400;
const MAX_RIGHT_W = 1100;

// Mac dinh rightWidth (pixel) tinh tu window.width — giu logic responsive cu.
// Tuong duong posLayoutSizes[1] (ratio 43-80) → quy doi sang pixel.
function computeDefaultRightWidth(w) {
    let R;
    if (w > 2400) R = 43;
    else if (w > 2200) R = 45;
    else if (w > 2000) R = 50;
    else if (w > 1800) R = 58;
    else if (w > 1600) R = 62;
    else if (w > 1400) R = 64;
    else if (w > 1300) R = 65;
    else if (w > 1200) R = 66;
    else if (w > 1100) R = 67;
    else if (w > 1000) R = 69;
    else if (w > 900) R = 72;
    else R = 80;
    return Math.round((w * R) / (100 + R));
}

export default forwardRef(function (props, ref) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const appGlobal = AntGlobal();
    const session = AntSession();

    const sessionValid = useMemo(() => {
        let _canRender = AntCondition.isValid(null, {
            whenSession: {
                session_status: { type: '!=', value: 'Restricted' }
            }
        });

        return _canRender;
    }, []);

    const refSpeaker = useRef();

    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    const handleResize = () => {
        setDimensions({
            width: window.innerWidth,
            height: window.innerHeight
        });
    };
    const [headerSearchConds, setHeaderSearchConds] = useState({});

    const [mainData, setMainData] = useState(null);
    const [voucherInstanceKey, setVoucherInstanceKey] = useState(0);
    const refMainData = useRef(null);

    const utilTabs = useMemo(() => {
        let tabs = props.utilTabs ? props.utilTabs : [];
        tabs = tabs.filter((t) => AntCondition.isVisible(null, t));
        return tabs;
    }, [props.utilTabs]);

    const header = useRef();
    const leftArea = useRef();
    const mainVoucher = useRef();
    const socketReloadTimer = useRef(null);

    // rightWidth (pixel) cho panel phai. Persist vao localStorage de giu giua
    // cac session. Default tu computeDefaultRightWidth dua tren window width.
    const [rightWidth, setRightWidth] = useState(() => {
        try {
            const stored = Number(localStorage.getItem(STORAGE_KEY_RIGHT_W));
            if (Number.isFinite(stored) && stored >= MIN_RIGHT_W && stored <= MAX_RIGHT_W) {
                return stored;
            }
        } catch (e) { /* ignore */ }
        return computeDefaultRightWidth(window.innerWidth);
    });
    const handleResizeRight = useCallback((w) => {
        setRightWidth(w);
        try { localStorage.setItem(STORAGE_KEY_RIGHT_W, String(w)); } catch (e) { /* ignore */ }
    }, []);

    const postAction = useCallback(async (action, code, id) => {
        if (mainVoucher.current) {
            return mainVoucher.current.postAction(action, code, id);
        }
    }, []);

    const runPrint = useCallback((strTemplate, options) => {
        if (mainVoucher.current) {
            return mainVoucher.current.runPrint(strTemplate, options);
        }
    }, []);

    const applyPreparedData = useCallback((payload) => {
        return mainVoucher.current?.applyPreparedData?.(payload) === true;
    }, []);

    const showAlert = useCallback((options) => {
        return mainVoucher.current?.showAlert?.(options);
    }, []);

    const autoReload = useCallback(() => {
        if (mainVoucher.current) {
            return mainVoucher.current.autoReload();
        }
    }, []);

    const showPopup = useCallback((ref, withMain, defaultObj) => {
        if (mainVoucher.current) {
            return mainVoucher.current.showPopup(ref, withMain, defaultObj);
        }
    }, []);

    const onActionCreate = useCallback(
        (done) => {
            if (mainVoucher.current) {
                setTimeout(() => {
                    mainVoucher.current.actionQuickCreate(done);
                }, 500);
            } else if (done) done();

            if (props.afterActionCreate) props.afterActionCreate();
        },
        [props.afterActionCreate]
    );

    const onAfterAction = useCallback(
        (e, data) => {
            if (e === 'delete') {
                setMainData(null);
                // Delete destroys the current voucher state. Remount an empty
                // instance immediately so subsequent create/open commands receive
                // a fresh imperative ref instead of the deleted voucher's ref.
                setVoucherInstanceKey((key) => key + 1);
                setTimeout(() => header.current?.reload?.(), 0);
            }
            if (props.onAfterAction) props.onAfterAction(e, data);
        },
        [props.onAfterAction]
    );

    const reloadTabGrid = useCallback((name) => {
        if (mainVoucher.current) {
            return mainVoucher.current.reloadTabGrid(name);
        }
    }, []);

    const reload = useCallback((e, test) => {
        if (mainVoucher.current && (e?.id || e?.code)) {
            let data = mainVoucher.current.getFormData();
            if ((data?.id || data?.code) && (e?.id === data?.id || e?.code === data?.code)) {
                mainVoucher.current.load(e);
            } else {
                setMainData(e);
            }
        } else {
            setMainData(e);
        }
    }, []);

    // const reload = useCallback((e) => {
    //     setMainData(e);
    // }, []);

    const closeAll = useCallback(() => {
        setMainData(null);
        setHeaderSearchConds({});
        setVoucherInstanceKey((key) => key + 1);
    }, []);

    const getFormData = useCallback(() => {
        if (mainVoucher.current) return mainVoucher.current.getFormData();

        return mainData;
    }, [mainData]);

    const selectTab = useCallback(
        (tabName) => {
            if (leftArea.current) {
                if (utilTabs.some((t) => t.name === tabName)) {
                    leftArea.current.selectTab(tabName);
                }
            }
        },
        [utilTabs]
    );

    const refreshUtilTabs = useCallback(() => {
        if (leftArea.current) {
            leftArea.current.refreshAll();
        }
    }, []);
    const refreshUtilBadges = useCallback(() => {
        if (leftArea.current && leftArea.current.refreshBadges) {
            leftArea.current.refreshBadges();
        }
    }, []);
    const patchUtilTabRows = useCallback((tabName, rows, options) => {
        return leftArea.current?.patchTabRows?.(tabName, rows, options) || false;
    }, []);
    const getUtilTabSearchConditions = useCallback((tabName) => {
        return leftArea.current?.getTabSearchConditions?.(tabName) || null;
    }, []);
    const refreshUtilTabRows = useCallback((tabName, searchPatch, options) => {
        return leftArea.current?.refreshTabRows?.(tabName, searchPatch, options) || Promise.resolve([]);
    }, []);
    const getSelectedTab = useCallback(() => {
        if (leftArea.current) {
            return leftArea.current.getSelectedTab();
        }
        return null;
    }, []);

    const searchByHeader = useCallback((cond) => {
        setHeaderSearchConds({ ...cond });
    }, []);

    const onHearMainCodeChanged = useCallback(
        (data) => {
            let newCode = data?.code ? data?.code : data;
            setMainData(typeof data === 'object' ? { ...data, code: newCode } : { code: newCode });
        },
        []
    );

    const showToast = useCallback(
        (data, opts) => {
            if (session?.showToast) {
                let isShowToast = props.showToastActivities ? props.showToastActivities.includes(data?.data?.activity) : true;
                let isPlaySound = props.playSoundActivities ? props.playSoundActivities.includes(data?.data?.activity) : true;
                let isPlayTextToSpeech = props.textToSpeechActivities ? props.textToSpeechActivities.includes(data?.data?.activity) : true;

                if (data?.toast_enable === false) isShowToast = false;
                if (data?.sound_enable === false) isPlaySound = false;
                if (data?.speech_enable === false) isPlayTextToSpeech = false;

                let speakTimes = data?.speech_times ? data?.speech_times : opts?.speakTimes;

                return session?.showToast(data, props.onToastClicked, {
                    showToast: isShowToast,
                    playSound: isPlaySound,
                    speakVoice: isPlayTextToSpeech,
                    speakTimes: speakTimes
                });
            }
        },
        [props.onToastClicked, props.showToastActivities, props.playSoundActivities, props.textToSpeechActivities]
    );

    const speak = useCallback((text) => {
        if (refSpeaker.current) {
            if (refSpeaker.current.canSpeak()) {
                refSpeaker.current.speak(text);
            }
        }
    }, []);

    useEffect(() => {
        if (props.onMainCodeChanged) props.onMainCodeChanged(mainData);
    }, [mainData]);

    useEffect(() => {
        if (params?.code) {
            reload({ code: params?.code });
        }
    }, [location]);

    const focusSearchBox = useCallback(() => {
        if (leftArea.current) leftArea.current.focusSearchBox();
    }, []);

    useImperativeHandle(ref, () => ({
        selectTab: selectTab,
        getSelectedTab: getSelectedTab,
        refreshUtilTabs: refreshUtilTabs,
        refreshUtilBadges: refreshUtilBadges,
        patchUtilTabRows: patchUtilTabRows,
        getUtilTabSearchConditions: getUtilTabSearchConditions,
        refreshUtilTabRows: refreshUtilTabRows,
        isUtilTabReady: (tabName) => Boolean(leftArea.current?.isTabReady?.(tabName)),
        reload: reload,
        applyPreparedData: applyPreparedData,
        autoReload: autoReload,
        runPrint: runPrint,
        showAlert: showAlert,
        showPopup: showPopup,
        getFormData: getFormData,
        postAction: postAction,
        searchByHeader: searchByHeader,
        closeAll: closeAll,
        showToast: showToast,
        speak: speak,
        focusSearchBox: focusSearchBox,
        reloadTabGrid: reloadTabGrid
    }));

    // Clamp rightWidth theo viewport: giu panel trai >= MIN_LEFT_W, panel phai
    // trong [MIN_RIGHT_W, MAX_RIGHT_W]. User co the keo nho lai sau khi resize.
    const effectiveRightWidth = useMemo(() => {
        const maxAllowed = Math.max(MIN_RIGHT_W, Math.min(MAX_RIGHT_W, dimensions.width - MIN_LEFT_W));
        return Math.max(MIN_RIGHT_W, Math.min(maxAllowed, rightWidth));
    }, [rightWidth, dimensions.width]);

    const splitterMax = useMemo(() => {
        return Math.max(MIN_RIGHT_W, Math.min(MAX_RIGHT_W, dimensions.width - MIN_LEFT_W));
    }, [dimensions.width]);

    const splitterDefault = useMemo(() => computeDefaultRightWidth(dimensions.width), [dimensions.width]);

    // QUAN TRONG: KHONG dung effectiveRightWidth cho posLayoutSizes vi:
    //   1. effectiveRightWidth thay doi MOI PIXEL keo splitter
    //   2. → posLayoutSizes ref moi moi tick
    //   3. → useMemo bao HeaderArea invalidate → HeaderArea re-render
    //   4. → useEffect([posLayoutSizes]) trong HeaderArea goi headerBox.repaint()
    //   5. → DevExtreme Box repaint -> ant-pos-tab-item flicker, mat CSS
    //
    // Fix: stableRightWidth = debounced version, chi commit 150ms SAU khi user
    // dung keo. Trong qua trinh keo, posLayoutSizes giu nguyen → HeaderArea
    // hoan toan tinh. Sau khi keo xong moi update mot lan duy nhat.
    const [stableRightWidth, setStableRightWidth] = useState(rightWidth);
    useEffect(() => {
        const t = setTimeout(() => setStableRightWidth(rightWidth), 150);
        return () => clearTimeout(t);
    }, [rightWidth]);

    const effectiveStableRightWidth = useMemo(() => {
        const maxAllowed = Math.max(MIN_RIGHT_W, Math.min(MAX_RIGHT_W, dimensions.width - MIN_LEFT_W));
        return Math.max(MIN_RIGHT_W, Math.min(maxAllowed, stableRightWidth));
    }, [stableRightWidth, dimensions.width]);

    // Quantize the header ratio so it only repaints after crossing a meaningful
    // width threshold. Buckets above 80 are required when the right panel is
    // wider than half of the screen.
    const POS_LAYOUT_BUCKETS = useRef([
        43, 45, 50, 58, 62, 64, 65, 66, 67, 69, 75, 80, 90, 100, 110, 125, 150, 175, 200, 225, 250, 275
    ]);
    const posLayoutSizesRef = useRef([100, 80]);
    const posLayoutSizes = useMemo(() => {
        const leftPx = Math.max(1, dimensions.width - effectiveStableRightWidth);
        let R = Math.max(1, Math.round((100 * effectiveStableRightWidth) / leftPx));
        const buckets = POS_LAYOUT_BUCKETS.current;
        R = buckets.find((t) => R <= t) || buckets[buckets.length - 1];
        const cached = posLayoutSizesRef.current;
        if (cached[1] === R) return cached;
        const next = [100, R];
        posLayoutSizesRef.current = next;
        return next;
    }, [effectiveStableRightWidth, dimensions.width]);

    // HeaderArea uses a DevExtreme Box whose two items are separated by the
    // 12px CSS gap. Calculate the real width of its right item instead of
    // assuming that it always equals the voucher panel width. This matters
    // especially when the splitter moves the right panel past 50%.
    const headerRightWidth = useMemo(() => {
        return getHeaderRightWidth(dimensions.width, posLayoutSizes);
    }, [dimensions.width, posLayoutSizes]);

    // console.log('ant-log-posLayoutSizes : ',posLayoutSizes);

    const onSocketReceived = useCallback(
        (data, screenName) => {
            if (data?.byPassPOSMode) return false;

            if (data?.event === 'notification' && props.pathName === screenName) {
                // Bản tin món mới mang sẵn dữ liệu dòng: cho màn vá danh sách TRƯỚC khi hiện
                // toast (giống POS), đừng bắt màn chờ bản tin dữ liệu đến sau vài giây.
                if (props.onSocketReceived) {
                    try {
                        props.onSocketReceived(data, screenName);
                    } catch (error) {
                        console.warn('AntPOSScreen socket handler failed:', error);
                    }
                }
                return showToast({ ...data, text: data.message, textToSpeech: data.speechMessage });
            }

            const isReconnected = data?.event === 'SOCKET_RECONNECTED';
            const isCurrentVoucher = props.API?.entityName === data?.name && refMainData.current?.code === data?.data?.code;
            if (isReconnected || isCurrentVoucher) {
                const isRecalculated = data.event === `${data.name}-Recalculated`;
                if (mainVoucher.current) {
                    // Recalculated: chi cap nhat footer + actions, KHONG reload grid.
                    // Payload co san totals (BE dinh kem) → refreshSummary ap thang, 0 API call.
                    if (isRecalculated && mainVoucher.current.refreshSummary) {
                        mainVoucher.current.refreshSummary({ isRefresh: true, payload: data.data, skipPrepare: true });
                    }

                    // Quick action gửi full snapshot hoặc delta có revision. Nếu áp được
                    // thì không cần gọi updatePrepare lần nữa; mismatch/client cũ vẫn reload.
                    if (isCurrentVoucher && mainVoucher.current.applyPreparedData?.(data.data)) {
                        if (socketReloadTimer.current) {
                            clearTimeout(socketReloadTimer.current);
                            socketReloadTimer.current = null;
                        }
                        return false;
                    }

                    // Socket chỉ là tín hiệu tối ưu, không phải nguồn chân lý duy nhất. Gộp
                    // mọi action/update thành một lượt đối chiếu; AntPOSVoucher sẽ bỏ response
                    // cũ và không setState nếu snapshot giống hệt nên web không bị nháy.
                    if (socketReloadTimer.current) clearTimeout(socketReloadTimer.current);
                    socketReloadTimer.current = setTimeout(() => {
                        socketReloadTimer.current = null;
                        mainVoucher.current?.reload?.({ isRefresh: true });
                    }, 400);
                    return false;
                }
            }
            if (props.onSocketReceived) props.onSocketReceived(data, screenName);
        },
        [showToast, props.API, props.onSocketReceived, props.pathName]
    );

    useEffect(() => {
        window.addEventListener('resize', handleResize, false);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        session.clearSocketListener(props.pathName);
        session.registerSocketListener(onSocketReceived, props.pathName);
        return () => {
            session.clearSocketListener(props.pathName);
            if (socketReloadTimer.current) {
                clearTimeout(socketReloadTimer.current);
                socketReloadTimer.current = null;
            }
        };
    }, [onSocketReceived]);

    useEffect(() => {
        refMainData.current = mainData;
    }, [mainData]);

    return (
        <React.Fragment>
            <div className="ant-pos-screen">
                {useMemo(
                    () => (
                        <HeaderArea
                            ref={header}
                            title={props.title}
                            module={props.module}
                            table={props.table}
                            API={props.API}
                            {...props.voucherTabs}
                            mainData={mainData}
                            onMainCodeChanged={onHearMainCodeChanged}
                            onActionCreate={onActionCreate}
                            onCustomText={props.voucherTabs?.onCustomText}
                            selectFields={props.voucherTabs?.selectFields}
                            requireFields={props.voucherTabs?.requireFields}
                            posLayoutSizes={posLayoutSizes}
                            rightPanelWidth={effectiveStableRightWidth}
                            headerRightWidth={headerRightWidth}
                            searchConditions={headerSearchConds}
                            hideTabs={Boolean(!sessionValid || props.hideVoucherTabs || props.renderRightArea)}
                            headerLeftExtras={props.headerLeftExtras}
                        />
                    ),
                    [
                        props.title,
                        props.module,
                        props.table,
                        props.API,
                        props.voucherTabs,
                        mainData,
                        onHearMainCodeChanged,
                        onActionCreate,
                        posLayoutSizes,
                        effectiveStableRightWidth,
                        headerRightWidth,
                        headerSearchConds,
                        sessionValid,
                        props.hideVoucherTabs,
                        props.renderRightArea,
                        props.headerLeftExtras
                    ]
                )}

                {!sessionValid && (
                    <div style={{ backgroundColor: 'white', flex: 1 }}>
                        <AntErrorPage
                            // id={`ant-search-error-page-${props.API.entityName}`}
                            text={AntLocalization.translateText({
                                vi: 'Không có quyền truy cập !',
                                en: 'Access Denied',
                                fr: 'Accès refusé',
                                es: 'Acceso denegado',
                                id: 'Akses ditolak',
                                ja: 'アクセス拒否',
                                zh: '访问被拒绝',
                                ko: '접근 거부',
                                hi: 'पहुँच अस्वीकृत',
                                ar: 'تم رفض الوصول'
                            })}
                        />
                    </div>
                )}
                {sessionValid && props.showOverlay && (
                    <div
                        style={{
                            backgroundColor: 'var(--AnteAppColor) !important',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: 0,
                                height: 'fit-content'
                            }}
                        >
                            <img
                                style={{
                                    width: '30vw',
                                    // opacity:  0.4,
                                    marginBottom: '10vh'
                                }}
                                src={appGlobal.appAssets.images.logoWithBrandSlogunWhite}
                            ></img>
                        </div>
                    </div>
                )}

                {sessionValid && !props.showOverlay && (
                    <div className="ant-pos-screen-content">
                        <div className="ant-pos-screen-panel ant-pos-screen-panel--left">
                            {props.renderLeftArea ? props.renderLeftArea() : undefined}
                            {!props.renderLeftArea &&
                                useMemo(
                                    () => (
                                        <UtilsTabs
                                            ref={leftArea}
                                            tabs={utilTabs}
                                            badgeRefreshSeconds={props.badgeRefreshSeconds}
                                            badgeBatch={props.badgeBatch}
                                            onCellClick={props.onCellClick}
                                            onUtilAction={props.onUtilAction}
                                            afterUtilAction={props.afterUtilAction}
                                            onUtilTabChange={props.onUtilTabChange}
                                        />
                                    ),
                                    [
                                        utilTabs,
                                        props.badgeRefreshSeconds,
                                        props.badgeBatch,
                                        props.onCellClick,
                                        props.onUtilAction,
                                        props.afterUtilAction,
                                        props.onUtilTabChange
                                    ]
                                )}
                        </div>
                        <Splitter
                            rightWidth={effectiveRightWidth}
                            onChange={handleResizeRight}
                            min={MIN_RIGHT_W}
                            max={splitterMax}
                            defaultWidth={splitterDefault}
                        />
                        <div
                            className="ant-pos-screen-panel ant-pos-screen-panel--right"
                            style={{ flex: `0 0 ${effectiveRightWidth}px` }}
                        >
                            <div className="ant-pos-screen-main" id={'ant-pos-screen-main-id'}>
                                {props.renderRightArea ? props.renderRightArea() : undefined}
                                {!props.renderRightArea && !mainData && appGlobal?.appAssets?.images?.logoOnly && (
                                    <div className="ant-pos-screen-empty-order-logo" aria-hidden="true">
                                        <img src={appGlobal.appAssets.images.logoOnly} alt="" draggable="false" />
                                    </div>
                                )}
                                {!props.renderRightArea && (
                                    <AntPOSVoucher
                                        key={voucherInstanceKey}
                                        ref={mainVoucher}
                                        {...props}
                                        mainData={mainData}
                                        id={mainData?.id}
                                        code={mainData?.code}
                                        onNavigateToReload={setMainData}
                                        onAfterAction={onAfterAction}
                                        className={props.className}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {sessionValid && <AntTextToSpeech ref={refSpeaker} rate={1.1} />}
            </div>
        </React.Fragment>
    );
});
